import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { RouterService } from '../../../../utilities/router/router.service';
import { AreaService } from '../../../../resources/area/area.service';
import { Area } from '../../../../resources/area/area.interface';
import { BasicAreaService } from '../../../../resources/basic-area/basic-area.service';
import { ClusterService } from '../../../../resources/cluster/cluster.service';
import { Cluster } from '../../../../resources/cluster/cluster.interface';
import { ElementService } from '../../../../resources/element/element.service';
import { takeUntil } from 'rxjs/operators';
import { FormGroup, FormBuilder } from '@angular/forms';
import { AuthService } from '../../../../auth/auth.service';

@Component({
  selector: 'sg2-area-details-properties',
  templateUrl: './area-details-properties.component.html',
  styleUrls: ['./area-details-properties.component.less']
})
export class AreaDetailsPropertiesComponent implements  OnDestroy, OnInit {

  /** Current area. */
  public area: Area;

  /** Form fields for patching area properties. */
  public areaPropertiesForm: FormGroup;

  /** Current basic area. */
  public basicArea: BasicArea;

  /** List of all basic areas in current project. */
  public basicAreas: BasicArea[];

  /** Current cluster. */
  public cluster: Partial<Cluster>;

  /** List of all clusters in current project. */
  public clusters: Cluster[];

  /** Destroy lifecycle hook. */
  public destroy$ = new Subject();

  /** Route params. */
  private params: {
    areaId: Area['_id'],
    basicAreaId: BasicArea['_id'],
    buildingId: Building['_id'],
    projectId: Project['_id'],
  };

  constructor(
    @Inject('$sgLocation') private $sgLocation: any,
    public authService: AuthService,
    private areaService: AreaService,
    private basicAreaService: BasicAreaService,
    private clusterService: ClusterService,
    private elementService: ElementService,
    private formBuilder: FormBuilder,
    private routerService: RouterService,
  ) { }

  ngOnInit() {
    this.params = this.routerService.getParams();

    this.area = this.areaService.getArea(this.params.areaId);
    this.basicArea = this.basicAreaService.getBasicArea(this.area.basicAreaId);
    this.basicAreas = this.basicAreaService.get();
    this.cluster = this.clusterService.getCluster(this.area.clusterId);
    this.clusters = this.clusterService.get();

    this._initForm();
    this._onAreaChange();
    this._onAreaPropertiesChange();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Go to legacy basic area.
   *
   */
  public goToBasicArea(): void {
    this.$sgLocation.path(`/project/${ this.params.projectId }/basic-areas/${ this.area.basicAreaId }`);
  }

  /**
   * Go to legacy building.
   *
   * @todo Modify once the target page is refactored.
   * @todo Add cluster id as anchor to building page.
   */
  public goToLegacyBuilding(): void {
    this.$sgLocation.path(`/project/${ this.params.projectId }/building/${ this.params.buildingId }/areas`);
  }

  /**
   * Submit area properties form by keypress.
   *
   * After pressing enter, it simply blurs the form, causing the form observable to
   * pick up the blur event, and handles the patching.
   *
   * @param event Keyboard event.
   */
  public submitAreaProperty(event: KeyboardEvent | any): void {
    event.target.blur();
  }

  /**
   * Initialize properties form.
   */
  private _initForm(): void {
    this.areaPropertiesForm = this.formBuilder.group({
      quantity: this.area.quantity,
      surface: this.area.surface,
      height: this.area.height,
      occupation: this.area.occupation,
      cluster: { value: this.area.clusterId, disabled: true },
      basicArea: { value: this.area.basicAreaId, disabled: true }
    }, { updateOn: 'blur' });
  }

  /**
   * Track changes on area.
   */
  private _onAreaChange(): void {
    this.elementService.element$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.area = this.areaService.getArea(this.params.areaId);
    });
  }

  /**
   * Track changes on area properties form and patch area.
   */
  private _onAreaPropertiesChange(): void {
    this.areaPropertiesForm.valueChanges.subscribe(values => {
      this.areaService.patch(this.area, values).subscribe();
    });
  }
}
